import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  Avatar,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Link,
  Divider,
  TextField as MuiTextField,
  Autocomplete,
  Drawer,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RefreshIcon from '@mui/icons-material/Refresh';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import AuthContext from '../store/AuthContext';
import projects from '../data/project-data';
import { exportProjectsAsModule } from '../utils/projectUtils';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { isLocalEnvironment } from '../utils/environmentUtils';
import { processImageFile, saveProjectsLocally, loadProjectsLocally } from '../utils/fileUtils';

// Available tags for projects
const availableTags = [
  "React",
  "OpenAI",
  "Django",
  "Redux",
  "Node.js",
  "JavaScript",
  "AI",
  "jQuery",
  "C++",
  "Simulation",
  "Automation",
  "Figma",
  "Oracle",
  "Python",
  "Selenium",
  "Hackathon",
  "Sustainability",
  "UX Design",
  "Spotlight",
];

// Helper function to reorder projects when dragging
const reorderProjects = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

// Update the ensure project IDs function to guarantee string IDs
const ensureProjectIds = (projectsArray) => {
  return projectsArray.map((project, index) => {
    // If project doesn't have an id or id is not a string, assign one
    if (!project.id || typeof project.id !== 'string') {
      return { ...project, id: `project-${index + 1}` };
    }
    return project;
  });
};

function ProjectManager() {
  const { isAuthenticated, isLocalEnv } = useContext(AuthContext);
  const navigate = useNavigate();
  const [projectList, setProjectList] = useState(projects);
  const [previousProjectList, setPreviousProjectList] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [newTag, setNewTag] = useState('');
  const [customTags, setCustomTags] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [jsonDrawerOpen, setJsonDrawerOpen] = useState(false);
  
  // Check if we're in production, and redirect if necessary
  useEffect(() => {
    // If not on localhost, redirect to home
    if (!isLocalEnv) {
      navigate('/');
    }
  }, [isLocalEnv, navigate]);
  
  // If not on localhost, show access denied
  if (!isLocalEnv) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
            Access Denied
          </Typography>
          <Alert severity="warning" sx={{ mb: 3 }}>
            The Project Manager is only available in the local development environment.
            <br />
            This feature is disabled on the production website for security reasons.
          </Alert>
          <Button 
            variant="contained" 
            color="primary" 
            component={RouterLink} 
            to="/"
          >
            Back to Home
          </Button>
        </Box>
      </Container>
    );
  }

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    repoLink: '',
    screenshot: '',
    tags: [],
    description: '',
    liveUrl: '',
  });

  // Combine built-in tags with custom tags
  const allTags = [...availableTags, ...customTags];

  // Initialize custom tags from existing projects
  useEffect(() => {
    const projectTags = projectList.flatMap(project => project.tags || []);
    const uniqueCustomTags = [...new Set(projectTags.filter(tag => !availableTags.includes(tag)))];
    setCustomTags(uniqueCustomTags);
  }, []);

  // Load saved projects from localStorage if available
  useEffect(() => {
    const savedProjects = loadProjectsLocally();
    if (savedProjects && savedProjects.length > 0) {
      // Ensure all saved projects have string IDs
      const projectsWithIds = ensureProjectIds(savedProjects);
      setProjectList(projectsWithIds);
      console.log('Loaded projects with IDs:', projectsWithIds.map(p => p.id));
    } else {
      // Add IDs to default projects
      const defaultProjectsWithIds = ensureProjectIds(projects);
      setProjectList(defaultProjectsWithIds);
      console.log('Using default projects with IDs:', defaultProjectsWithIds.map(p => p.id));
    }
  }, []);

  // Update previous state when current state changes
  useEffect(() => {
    if (projectList) {
      // Only store the previous state when an actual update happens
      // (not on initial load)
      if (previousProjectList === null) {
        setPreviousProjectList(projectList);
      }
    }
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle tag selection
  const handleTagChange = (event) => {
    setFormData({
      ...formData,
      tags: event.target.value,
    });
  };

  // Handle adding a new custom tag
  const handleAddCustomTag = () => {
    if (newTag && !allTags.includes(newTag)) {
      setCustomTags([...customTags, newTag]);
      // Also add it to the current form's tags
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag]
      });
      setNewTag('');
    }
  };

  // Native drag and drop handlers
  const handleDragStart = (e, projectId) => {
    e.dataTransfer.setData('projectId', projectId);
    setDragging(projectId);
  };

  const handleDragOver = (e, projectId) => {
    e.preventDefault();
    setDragOverId(projectId);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    
    const draggedId = e.dataTransfer.getData('projectId');
    
    // If dropping on itself, do nothing
    if (draggedId === targetId) {
      setDragging(null);
      setDragOverId(null);
      return;
    }
    
    // Save current state for undo
    saveCurrentState();
    
    // Reorder projects
    const currentProjects = [...projectList];
    const draggedItemIndex = currentProjects.findIndex(project => project.id === draggedId);
    const targetIndex = currentProjects.findIndex(project => project.id === targetId);
    
    if (draggedItemIndex !== -1 && targetIndex !== -1) {
      const [removedItem] = currentProjects.splice(draggedItemIndex, 1);
      currentProjects.splice(targetIndex, 0, removedItem);
      setProjectList(currentProjects);
      
      // Save the reordered list to localStorage
      saveProjectsLocally(currentProjects).catch(error => {
        console.error('Error saving reordered projects:', error);
      });
      
      setSnackbar({
        open: true,
        message: 'Projects reordered successfully!',
        severity: 'success',
      });
    }
    
    setDragging(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    setDragging(null);
    setDragOverId(null);
  };

  // Open dialog for adding/editing a project
  const handleOpenDialog = (project = null) => {
    if (project) {
      setCurrentProject(project);
      setFormData({
        name: project.name,
        repoLink: project.repoLink || '',
        screenshot: project.screenshot || '',
        tags: project.tags || [],
        description: project.description || '',
        liveUrl: project.liveUrl || '',
      });
      setIsEditing(true);
    } else {
      setCurrentProject(null);
      setFormData({
        name: '',
        repoLink: '',
        screenshot: '',
        tags: [],
        description: '',
        liveUrl: '',
      });
      setIsEditing(false);
    }
    setSelectedFile(null);
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFile(null);
  };

  // Save the current state before making changes
  const saveCurrentState = () => {
    setPreviousProjectList([...projectList]);
  };

  // Undo the most recent change by restoring previous state
  const handleUndo = () => {
    if (previousProjectList) {
      // Save the current state to allow redoing by undoing the undo
      const currentState = [...projectList];
      
      // Restore the previous state
      setProjectList(previousProjectList);
      
      // Update localStorage with the restored state
      saveProjectsLocally(previousProjectList).catch(error => {
        console.error('Error saving projects after undo:', error);
      });
      
      // Set the previous state to the current state (to allow multiple undos)
      setPreviousProjectList(currentState);
      
      setSnackbar({
        open: true,
        message: 'Undo successful!',
        severity: 'success',
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Nothing to undo!',
        severity: 'info',
      });
    }
  };

  // Save project (add or update)
  const handleSaveProject = async () => {
    // Save current state for undo
    saveCurrentState();
    
    // Generate image path if a file is selected
    let screenshotPath = formData.screenshot;
    
    if (selectedFile) {
      try {
        // Process the image using our local utility instead of uploading to a server
        screenshotPath = await processImageFile(selectedFile, formData.name);
        
        setSnackbar({
          open: true,
          message: 'Image processed successfully',
          severity: 'success',
        });
      } catch (error) {
        console.error('Error processing file:', error);
        setSnackbar({
          open: true,
          message: `Error processing image: ${error.message}`,
          severity: 'error',
        });
        return; // Stop the function if file processing fails
      }
    }
    
    const updatedFormData = {
      ...formData,
      screenshot: screenshotPath,
    };
    
    let updatedProjects;
    
    if (isEditing) {
      // Update existing project, preserving its ID
      updatedProjects = projectList.map(project => 
        project === currentProject ? { ...updatedFormData, id: String(currentProject.id) } : project
      );
      setProjectList(updatedProjects);
      setSnackbar({
        open: true,
        message: 'Project updated successfully!',
        severity: 'success',
      });
    } else {
      // Add new project with a unique ID that follows the format project-{index}
      const maxIndex = projectList.reduce((max, project) => {
        // Extract the index number from the ID (if it follows our format)
        if (project.id && typeof project.id === 'string' && project.id.startsWith('project-')) {
          const idNumber = parseInt(project.id.replace('project-', ''), 10);
          return isNaN(idNumber) ? max : Math.max(max, idNumber);
        }
        return max;
      }, 0);
      
      const newProject = { 
        ...updatedFormData, 
        id: `project-${maxIndex + 1}` // Use the next available index
      };
      updatedProjects = [...projectList, newProject];
      setProjectList(updatedProjects);
      setSnackbar({
        open: true,
        message: 'Project added successfully!',
        severity: 'success',
      });
    }
    
    // Save projects locally instead of to a server
    try {
      await saveProjectsLocally(updatedProjects);
    } catch (error) {
      console.error('Error saving projects locally:', error);
      // We still continue as the state was updated
    }
    
    handleCloseDialog();
  };

  // Delete project
  const handleDeleteProject = (project) => {
    // Save current state for undo
    saveCurrentState();
    
    console.log('Deleting project with ID:', project.id);
    const updatedProjects = projectList.filter(p => p !== project);
    setProjectList(updatedProjects);
    
    // Save the updated list locally
    saveProjectsLocally(updatedProjects).catch(error => {
      console.error('Error saving projects after delete:', error);
    });
    
    setSnackbar({
      open: true,
      message: 'Project deleted successfully!',
      severity: 'success',
    });
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      
      // Create a temporary URL for the selected file to display it
      const imageUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        screenshot: imageUrl,
      });
    }
  };

  // Trigger file input click
  const handleImageUploadClick = () => {
    fileInputRef.current.click();
  };

  // Remove the current image
  const handleRemoveImage = () => {
    setSelectedFile(null);
    setFormData({
      ...formData,
      screenshot: '',
    });
  };

  // Export projects
  const handleExportProjects = () => {
    exportProjectsAsModule(projectList);
    setSnackbar({
      open: true,
      message: 'Projects exported successfully!',
      severity: 'success',
    });
  };

  // Handle refreshing projects from original data
  const handleRefreshProjects = () => {
    // First, clear localStorage to remove any saved modifications
    localStorage.removeItem('portfolio_projects');
    
    // Directly import the original projects data again to ensure we get fresh data
    // (bypassing any potential cached references)
    try {
      // Force a fresh import of the projects data
      const originalProjects = require('../data/project-data').default; // dont use the copy file.
      
      // Add IDs to default projects
      const defaultProjectsWithIds = ensureProjectIds(originalProjects);
      
      // Update the state with the fresh data
      setProjectList(defaultProjectsWithIds);
      
      // Log success
      console.log('Successfully reset to original project data');
      
      setSnackbar({
        open: true,
        message: 'Projects reset to original data!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error loading original project data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading original data!',
        severity: 'error',
      });
    }
  };

  // Toggle JSON drawer
  const toggleJsonDrawer = () => {
    setJsonDrawerOpen(!jsonDrawerOpen);
  };

  // Copy JSON to clipboard
  const copyJsonToClipboard = () => {
    // Remove id field from each project before stringifying
    const projectsWithoutIds = projectList.map(({ id, ...rest }) => rest);
    const jsonString = JSON.stringify(projectsWithoutIds, null, 2);
    
    navigator.clipboard.writeText(jsonString).then(
      () => {
        setSnackbar({
          open: true,
          message: 'JSON copied to clipboard!',
          severity: 'success',
        });
      },
      (err) => {
        console.error('Could not copy text: ', err);
        setSnackbar({
          open: true,
          message: 'Failed to copy to clipboard',
          severity: 'error',
        });
      }
    );
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          You need to be logged in as an admin to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Project Manager
        </Typography>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            component={RouterLink}
            to="/"
            sx={{ mr: 2 }}
          >
            Back to Home
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleExportProjects}
            sx={{ mr: 2 }}
          >
            Export Projects
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<UndoIcon />}
            onClick={handleUndo}
            sx={{ mr: 2 }}
          >
            Undo
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={handleRefreshProjects}
            sx={{ mr: 2 }}
          >
            Reset to Original
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={<CodeIcon />}
            onClick={toggleJsonDrawer}
            sx={{ mr: 2 }}
          >
            View JSON
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add New Project
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Drag and drop projects to reorder them. New order will be automatically saved.
      </Alert>

      {/* JSON Drawer */}
      <Drawer
        anchor="right"
        open={jsonDrawerOpen}
        onClose={toggleJsonDrawer}
        variant="persistent"
        sx={{
          width: 'min(600px, 50%)',
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 'min(600px, 50%)',
            boxSizing: 'border-box',
            position: 'fixed',
            height: '100%',
            zIndex: 1200,
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2, 
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" component="div">
            Projects JSON
          </Typography>
          <Box>
            <IconButton 
              color="primary" 
              onClick={copyJsonToClipboard} 
              title="Copy to clipboard"
              sx={{ mr: 1 }}
            >
              <ContentCopyIcon />
            </IconButton>
            <IconButton 
              onClick={toggleJsonDrawer}
              title="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ 
          p: 2, 
          height: 'calc(100% - 64px)',
          overflow: 'auto',
          backgroundColor: '#f5f5f5'
        }}>
          <pre
            style={{
              margin: 0,
              padding: 16,
              backgroundColor: '#2d2d2d',
              color: '#ffffff',
              borderRadius: 4,
              overflow: 'auto',
              fontSize: 14,
              fontFamily: '"Consolas", "Monaco", monospace'
            }}
          >
            {JSON.stringify(
              projectList.map(({ id, ...rest }) => rest),
              null,
              2
            )}
          </pre>
        </Box>
      </Drawer>

      {/* Main content container with conditional styling for drawer open state */}
      <Box sx={{ 
        marginRight: jsonDrawerOpen ? 'min(600px, 50%)' : 0,
        transition: 'margin 0.3s ease',
      }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {projectList.map((project) => (
            <Grid
              item
              xs={12}
              key={project.id}
              draggable
              onDragStart={(e) => handleDragStart(e, project.id)}
              onDragOver={(e) => handleDragOver(e, project.id)}
              onDrop={(e) => handleDrop(e, project.id)}
              onDragEnd={handleDragEnd}
              sx={{
                transition: 'transform 0.2s',
                transform: dragging === project.id ? 'scale(1.02)' : 'scale(1)',
                zIndex: dragging === project.id ? 1 : 0,
              }}
            >
              <Card 
                sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' }, 
                  height: '100%',
                  boxShadow: dragging === project.id ? '0 8px 16px rgba(0,0,0,0.1)' : undefined,
                  border: dragOverId === project.id ? '2px solid #1976d2' : 'none',
                }}
              >
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px',
                    cursor: 'grab',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                    backgroundColor: dragging === project.id ? 'rgba(0,0,0,0.03)' : undefined
                  }}
                >
                  <DragIndicatorIcon />
                </Box>
                <CardMedia
                  component="img"
                  sx={{ 
                    width: { xs: '100%', md: 250 },
                    height: { xs: 200, md: 'auto' },
                    objectFit: 'cover'
                  }}
                  image={project.screenshot}
                  alt={project.name}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <CardContent sx={{ flex: '1 0 auto' }}>
                    <Typography variant="h5" component="div" gutterBottom>
                      {project.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {project.description}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {project.tags && project.tags.map((tag, tagIndex) => (
                        <Chip key={tagIndex} label={tag} size="small" />
                      ))}
                    </Box>
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ justifyContent: 'space-between', padding: 2 }}>
                    <Box>
                      {project.repoLink && (
                        <Button 
                          component="a"
                          href={project.repoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<GitHubIcon />}
                          sx={{ mr: 1 }}
                        >
                          GitHub
                        </Button>
                      )}
                      {project.liveUrl && (
                        <Button 
                          component="a"
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<LaunchIcon />}
                        >
                          Live Demo
                        </Button>
                      )}
                    </Box>
                    <Box>
                      <IconButton onClick={() => handleOpenDialog(project)} color="primary" title="Edit">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteProject(project)} color="error" title="Delete">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Box>
              </Card>
            </Grid>
          ))}
          
          {/* Add Project Button at bottom of list */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Card 
              sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 100,
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                border: '1px dashed rgba(25, 118, 210, 0.5)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  borderColor: 'primary.main',
                },
              }}
              onClick={() => handleOpenDialog()}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', py: 3 }}>
                <AddIcon color="primary" sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant="h6" color="primary">
                  Add New Project
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Add/Edit Project Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{isEditing ? 'Edit Project' : 'Add New Project'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Project Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="GitHub Repo Link"
                  name="repoLink"
                  value={formData.repoLink}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Live URL"
                  name="liveUrl"
                  value={formData.liveUrl}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Project Image
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 150,
                        height: 100,
                        border: '1px dashed #ccc',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      {formData.screenshot ? (
                        <img 
                          src={formData.screenshot} 
                          alt="Project Preview" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <ImageIcon color="disabled" sx={{ fontSize: 40 }} />
                      )}
                    </Box>
                    <Box>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                      />
                      <Button
                        variant="outlined"
                        startIcon={<ImageIcon />}
                        onClick={handleImageUploadClick}
                      >
                        Select Image
                      </Button>
                      {formData.screenshot && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={handleRemoveImage}
                        >
                          Remove Image
                        </Button>
                      )}
                      {selectedFile && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Selected: {selectedFile.name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Tags</InputLabel>
                  <Select
                    multiple
                    value={formData.tags}
                    onChange={handleTagChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip 
                            key={value} 
                            label={value} 
                            onDelete={(event) => {
                              event.stopPropagation(); // Prevent opening the dropdown
                              setFormData({
                                ...formData,
                                tags: formData.tags.filter(tag => tag !== value)
                              });
                            }} 
                            onClick={(event) => event.stopPropagation()} // Prevent opening dropdown when clicking on chip
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {allTags.map((tag) => (
                      <MenuItem key={tag} value={tag}>
                        {tag}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    label="Add Custom Tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    sx={{ mr: 1, flexGrow: 1 }}
                  />
                  <Button 
                    variant="outlined" 
                    onClick={handleAddCustomTag}
                    disabled={!newTag || allTags.includes(newTag)}
                  >
                    Add Tag
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSaveProject} variant="contained" color="primary">
              {isEditing ? 'Update' : 'Add'} Project
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}

export default ProjectManager; 